// src/views/projects/ProjectCostView.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import { tokens } from "../../theme/tokens";
import { graphqlEndpoint } from "../../config";

import { READ_ALL_USERS } from "../../graph/users/queries";
import { READ_PROJECT } from "../../graph/projects/queries";

const client = new GraphQLClient(graphqlEndpoint);

function formatCurrency(amount, currencySymbol = "£") {
  const numericAmount = Number(amount);

  if (!Number.isFinite(numericAmount)) {
    return `${currencySymbol}0.00`;
  }

  return `${currencySymbol}${numericAmount.toFixed(2)}`;
}

function formatMinutes(minutes) {
  const numericMinutes = Number(minutes);

  if (!Number.isFinite(numericMinutes)) {
    return "0";
  }

  return numericMinutes.toLocaleString();
}

export default function ProjectCostView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [project, setProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [usePlannedFallback, setUsePlannedFallback] =
    useState(true);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const redirectPath =
    location.state?.redirectPath ||
    (id ? `/projects/edit/${id}` : "/manageProjects");

  const cameFromPlaybook =
    location.state?.source === "playbook";

  useEffect(() => {
    let isMounted = true;

    const loadCosts = async () => {
      setLoading(true);
      setLoadError("");

      try {
        const [
          { readSingleProjectById },
          { readAllUsers },
        ] = await Promise.all([
          client.request(READ_PROJECT, { id }),
          client.request(READ_ALL_USERS),
        ]);

        if (!isMounted) {
          return;
        }

        setProject(readSingleProjectById || null);
        setUsers(readAllUsers || []);
      } catch (error) {
  console.error("Failed to load costs:", error);
  console.error(
    "GraphQL errors:",
    error?.response?.errors
  );

  if (!isMounted) {
    return;
  }

  setLoadError(
    error?.response?.errors
      ?.map((item) => item.message)
      .join(", ") ||
      error?.message ||
      "Failed to load project costs"
  );
} finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (id) {
      loadCosts();
    } else {
      setLoadError("Project ID could not be resolved");
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [id]);

  const userById = useMemo(() => {
    const map = new Map();

    for (const user of users) {
      if (!user?.id) {
        continue;
      }

      const fullName = `${user.firstName || ""} ${
        user.lastName || ""
      }`.trim();

      const hourlyRate =
        user.hourlyRate === null ||
        user.hourlyRate === undefined ||
        user.hourlyRate === ""
          ? null
          : Number(user.hourlyRate);

      map.set(user.id, {
        id: user.id,
        fullName: fullName || "Unnamed user",
        hourlyRate:
          hourlyRate !== null &&
          Number.isFinite(hourlyRate)
            ? hourlyRate
            : null,
      });
    }

    return map;
  }, [users]);

  const rows = useMemo(() => {
    if (!project?.tasks) {
      return [];
    }

    return project.tasks
      .filter((task) => task.isLatest !== false)
      .map((task) => {
        const assignedUserId =
          task.assignedTenantUserId || null;

        const assignedUser = assignedUserId
          ? userById.get(assignedUserId)
          : null;

        const configuredRate =
          assignedUser?.hourlyRate ?? null;

        const rate = configuredRate ?? 0;

        const actualMin =
          task.actualDurationMin === null ||
          task.actualDurationMin === undefined
            ? null
            : Number(task.actualDurationMin);

        const plannedMin =
          task.plannedDurationMin === null ||
          task.plannedDurationMin === undefined
            ? 0
            : Number(task.plannedDurationMin);

        const minutesForCalc =
          actualMin !== null &&
          Number.isFinite(actualMin)
            ? actualMin
            : usePlannedFallback &&
                Number.isFinite(plannedMin)
              ? plannedMin
              : 0;

        const labourCost =
          (minutesForCalc * rate) / 60;

        const otherCost = Math.max(
          0,
          Number(task.cost ?? 0)
        );

        return {
          id: task.id,
          title: task.title || "Untitled task",
          department: task.department || "—",

          assignedTenantUserId: assignedUserId,

          assignedTo:
            assignedUser?.fullName ||
            task.assignedTo ||
            "Unassigned",

          assignmentResolved: Boolean(assignedUser),

          configuredRate,
          rate,

          plannedMin:
            Number.isFinite(plannedMin)
              ? plannedMin
              : 0,

          actualMin:
            actualMin !== null &&
            Number.isFinite(actualMin)
              ? actualMin
              : null,

          minutesForCalc,
          labourCost:
            Number.isFinite(labourCost)
              ? labourCost
              : 0,
          otherCost:
            Number.isFinite(otherCost)
              ? otherCost
              : 0,

          status: task.status,
        };
      });
  }, [
    project,
    userById,
    usePlannedFallback,
  ]);

  const totals = useMemo(() => {
    let totalMin = 0;
    let totalLabour = 0;
    let totalOther = 0;

    for (const row of rows) {
      totalMin += row.minutesForCalc || 0;
      totalLabour += row.labourCost || 0;
      totalOther += row.otherCost || 0;
    }

    return {
      totalMin,
      totalLabour,
      totalOther,
      projectTotal: totalLabour + totalOther,
    };
  }, [rows]);

  const rowsByDept = useMemo(() => {
    const map = new Map();

    for (const row of rows) {
      const department =
        row.department || "—";

      if (!map.has(department)) {
        map.set(department, []);
      }

      map.get(department).push(row);
    }

    return Array.from(map.entries());
  }, [rows]);

  const handleBack = () => {
    navigate(redirectPath);
  };

  if (loading) {
    return (
      <Box>
        <Header
          title="PROJECT COSTS"
          subtitle="Loading cost information"
        />

        <Typography mt={3}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box>
        <Header
          title="PROJECT COSTS"
          subtitle="Unable to load cost information"
        />

        <Typography
          mt={3}
          color="error.main"
        >
          {loadError}
        </Typography>

        <Box mt={2}>
          <ActionButton
            variant="outlined"
            tone="teal"
            onClick={handleBack}
          >
            {cameFromPlaybook
              ? "Back to Playbook"
              : "Back to Project"}
          </ActionButton>
        </Box>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box>
        <Header
          title="PROJECT COSTS"
          subtitle="Project not found"
        />

        <Typography mt={3}>
          The requested project could not be found.
        </Typography>

        <Box mt={2}>
          <ActionButton
            variant="outlined"
            tone="teal"
            onClick={handleBack}
          >
            {cameFromPlaybook
              ? "Back to Playbook"
              : "Back to Project"}
          </ActionButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Header
        title="PROJECT COSTS"
        subtitle={`Live labour cost view — ${project.title}`}
      />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        mb={2}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
        >
          {project.labels?.map((label) => (
            <Chip
              key={label}
              size="small"
              label={label}
            />
          ))}
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
        >
          <FormControlLabel
            control={
              <Switch
                checked={usePlannedFallback}
                onChange={(event) =>
                  setUsePlannedFallback(
                    event.target.checked
                  )
                }
              />
            }
            label="Use planned minutes when actuals are missing"
          />

          <ActionButton
            variant="outlined"
            tone="teal"
            onClick={handleBack}
          >
            {cameFromPlaybook
              ? "Back to Playbook"
              : "Back to Project"}
          </ActionButton>
        </Box>
      </Box>

      <Paper
        sx={{
          p: 2,
          mb: 2,
          border: `1px solid ${colors.scalpelTeal[400]}`,
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 0.5 }}
        >
          Totals
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
        >
          Minutes:{" "}
          <b>
            {formatMinutes(totals.totalMin)}
          </b>
          &nbsp;|&nbsp; Labour:{" "}
          <b>
            {formatCurrency(
              totals.totalLabour
            )}
          </b>
          &nbsp;|&nbsp; Other costs:{" "}
          <b>
            {formatCurrency(
              totals.totalOther
            )}
          </b>
          &nbsp;|&nbsp;{" "}
          <i>Project total</i>:{" "}
          <b>
            {formatCurrency(
              totals.projectTotal
            )}
          </b>
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
        >
          Labour uses the hourly rate configured
          against each assigned user. Other costs
          come from each task’s <code>cost</code>{" "}
          field.
        </Typography>
      </Paper>

      {rows.length === 0 && (
        <Paper
          sx={{
            p: 3,
            border: `1px solid ${colors.surface.border}`,
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="body1">
            This project does not currently contain
            any tasks.
          </Typography>
        </Paper>
      )}

      {rowsByDept.map(
        ([department, departmentRows]) => {
          const deptMin =
            departmentRows.reduce(
              (sum, row) =>
                sum +
                (row.minutesForCalc || 0),
              0
            );

          const deptLabour =
            departmentRows.reduce(
              (sum, row) =>
                sum +
                (row.labourCost || 0),
              0
            );

          const deptOther =
            departmentRows.reduce(
              (sum, row) =>
                sum +
                (row.otherCost || 0),
              0
            );

          const deptTotal =
            deptLabour + deptOther;

          return (
            <Paper
              key={department}
              sx={{
                p: 2,
                mb: 2,
                border: `1px solid ${colors.surface.border}`,
                bgcolor: "background.paper",
                overflowX: "auto",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="baseline"
                gap={2}
                flexWrap="wrap"
                mb={1}
              >
                <Typography variant="h6">
                  {department}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Minutes:{" "}
                  <b>
                    {formatMinutes(deptMin)}
                  </b>
                  &nbsp;|&nbsp; Labour:{" "}
                  <b>
                    {formatCurrency(
                      deptLabour
                    )}
                  </b>
                  &nbsp;|&nbsp; Other:{" "}
                  <b>
                    {formatCurrency(
                      deptOther
                    )}
                  </b>
                  &nbsp;|&nbsp; Total:{" "}
                  <b>
                    {formatCurrency(
                      deptTotal
                    )}
                  </b>
                </Typography>
              </Box>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      Task
                    </TableCell>

                    <TableCell>
                      Assigned
                    </TableCell>

                    <TableCell align="right">
                      Rate/hr
                    </TableCell>

                    <TableCell align="right">
                      Planned min
                    </TableCell>

                    <TableCell align="right">
                      Actual min
                    </TableCell>

                    <TableCell align="right">
                      Used min
                    </TableCell>

                    <TableCell align="right">
                      Labour £
                    </TableCell>

                    <TableCell align="right">
                      Other £
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {departmentRows.map(
                    (row) => (
                      <TableRow
                        key={row.id}
                        hover
                      >
                        <TableCell>
                          {row.title}
                        </TableCell>

                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {row.assignedTo}
                            </Typography>

                            {row.assignedTenantUserId &&
                              !row.assignmentResolved && (
                                <Typography
                                  variant="caption"
                                  color="warning.main"
                                >
                                  Assigned user could
                                  not be resolved
                                </Typography>
                              )}
                          </Box>
                        </TableCell>

                        <TableCell align="right">
                          {row.configuredRate !==
                          null
                            ? formatCurrency(
                                row.configuredRate
                              )
                            : "Not set"}
                        </TableCell>

                        <TableCell align="right">
                          {formatMinutes(
                            row.plannedMin
                          )}
                        </TableCell>

                        <TableCell align="right">
                          {row.actualMin !== null
                            ? formatMinutes(
                                row.actualMin
                              )
                            : "—"}
                        </TableCell>

                        <TableCell align="right">
                          {formatMinutes(
                            row.minutesForCalc
                          )}
                        </TableCell>

                        <TableCell align="right">
                          {formatCurrency(
                            row.labourCost
                          )}
                        </TableCell>

                        <TableCell align="right">
                          {formatCurrency(
                            row.otherCost
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </Paper>
          );
        }
      )}
    </Box>
  );
}